import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Button, Card, Spinner, Row, Col } from "react-bootstrap";
import SpinnerLoader from "../../../Components/SpinnerLoader";
import { checkSubscriptionStatus } from "../../../Services/StripeService";
import axios from "axios";
import { getAllSupportTeamMembers } from "../../../Services/LeaderSupportTeamMember";
import { getUserDetailsAPI } from "../../../Utilities/APIHelper";
import { get } from "../../../Services/AxiosService";

// Helper function to parse query parameters for hash router
const useQuery = () => {
  const location = useLocation();
  // With hash router, we need to parse the query part that comes after the hash path
  const searchParams = location.search || "";
  return new URLSearchParams(searchParams);
};

export default function SubscriptionSuccess() {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [processingStep, setProcessingStep] = useState(1); // Track current step
  const [teamMemberCount, setTeamMemberCount] = useState(0);
  const [isCheckoutVerified, setIsCheckoutVerified] = useState(false);
  const [actualSessionId, setActualSessionId] = useState(null);
  const [userEmail, setUserEmail] = useState(""); // Add state for user email
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  let sessionId = query.get("session_id");
  const userId = query.get("userId");
  const priceId = query.get("price_id");
  const API_URL =
    process.env.REACT_APP_API_STRIPE_URL || "http://localhost:3001";

  // Get user details to obtain email
  useEffect(() => {
    // Fetch the user's email from the API
    const getUserInfo = async () => {
      try {
        const res = await get(getUserDetailsAPI);
        const response = res.data.response;
        setUserEmail(response.email);
        console.log("Got user email:", response.email);
      } catch (err) {
        console.error("Failed to get user details:", err);
      }
    };

    getUserInfo();
  }, []);

  // Get session ID and handle URL encoding issues
  useEffect(() => {
    // Log the raw query params for debugging
    console.log("Raw location:", location);
    console.log("Raw query:", query);
    console.log("Session ID from query:", sessionId);

    // Handle URL-encoded session ID (if the {CHECKOUT_SESSION_ID} wasn't replaced)
    if (sessionId === "{CHECKOUT_SESSION_ID}") {
      // This means Stripe didn't replace the placeholder with the actual session ID
      // Try to get it from the URL fragment if present
      const hashParams = new URLSearchParams(
        window.location.hash.split("?")[1] || ""
      );
      const hashSessionId = hashParams.get("session_id");

      if (hashSessionId && hashSessionId !== "{CHECKOUT_SESSION_ID}") {
        console.log("Found session ID in hash fragment:", hashSessionId);
        // Store the actual session ID
        setActualSessionId(hashSessionId);
      } else {
        setStatus("error");
        setMessage(
          "Invalid session information. The Stripe checkout session ID was not properly returned."
        );
      }
    } else if (sessionId) {
      // If we have a valid session ID, save it
      setActualSessionId(sessionId);
    }
  }, [location, sessionId]); // Only depend on location and sessionId

  // Verify the checkout and process subscription
  useEffect(() => {
    // Skip if already verified or no session ID or no email yet
    if (isCheckoutVerified || !actualSessionId || !userEmail) {
      return;
    }

    const processSubscription = async () => {
      // Immediately mark as verified to prevent re-runs
      setIsCheckoutVerified(true);

      try {
        // Skip Step 1 (verification) and go directly to Step 2
        setProcessingStep(1);
        console.log("Getting team member count");

        // Get current team member count
        const teamMembersResponse = await getAllSupportTeamMembers();
        const teamMembersResult = JSON.parse(teamMembersResponse.response);

        let count = 0;
        if (
          teamMembersResult &&
          teamMembersResult.activeSupportTeamCount !== undefined
        ) {
          count = teamMembersResult.activeSupportTeamCount;
          setTeamMemberCount(count);
        }
        console.log("Current team member count:", count);

        // Step 2: Report the initial meter usage
        setProcessingStep(2);
        console.log("Reporting initial usage", {
          email: userEmail, // Use email instead of customerId
          supportTeamCount: count,
          priceId: priceId || "price_1RVjU0PrutU1IoZlwJW4XUzc", // Use default if not provided
        });

        // Report usage with email instead of customerId
        const usageResponse = await axios.post(
          `${API_URL}/api/stripe/report-metered-usage`,
          {
            email: userEmail, // Send email instead of customerId
            supportTeamCount: count,
            priceId: priceId || "price_1RVjU0PrutU1IoZlwJW4XUzc",
          }
        );

        if (!usageResponse.data.success) {
          throw new Error(
            usageResponse.data.message || "Failed to report initial usage"
          );
        }

        console.log("Usage reported successfully:", usageResponse.data);

        // Create a simplified subscription info object
        const subscriptionInfo = {
          plan: "Pay-As-You-Go",
          startDate: new Date().toISOString(),
        };

        setSubscriptionDetails(subscriptionInfo);
        setProcessingStep(4);
        setStatus("success");
        setMessage("Your subscription has been set up successfully!");

        // After 30 seconds, redirect to dashboard
        setTimeout(() => {
          handleContinueToDashboard();
        }, 30000);
      } catch (err) {
        console.error(`Error in step ${processingStep}:`, err);
        setStatus("error");
        setMessage(
          `Error in step ${processingStep}: ${
            err.message ||
            "An unexpected error occurred while setting up your subscription."
          } Please contact support.`
        );
      }
    };

    processSubscription();
  }, [actualSessionId, isCheckoutVerified, priceId, userEmail, processingStep]);

  const handleContinueToDashboard = () => {
    // Navigate properly with hash router
    history.push("/groupleader/groupleaderdashboard");
  };

  return (
    <div className="container p-0">
      <h1 className="page-title text-center my-4">Subscription Status</h1>

      <div className="dashboard-wrapper">
        <Card
          className="mx-auto shadow border-0"
          style={{
            maxWidth: "600px",
            borderRadius: "1rem",
          }}
        >
          <Card.Header
            as="h4"
            className="text-white py-4 text-center"
            style={{
              background: "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          >
            {status === "success"
              ? "Subscription Activated"
              : status === "error"
              ? "Subscription Error"
              : "Processing Subscription"}
          </Card.Header>
          <Card.Body className="text-center px-md-5 py-5">
            {status === "loading" && (
              <>
                <div className="mb-4">
                  <Spinner
                    animation="border"
                    variant="primary"
                    style={{ width: "4rem", height: "4rem", color: "#2660a5" }}
                  />
                </div>
                <h2 className="mb-3">Processing Your Subscription</h2>
                <div
                  className="progress mb-4"
                  style={{ height: "10px", borderRadius: "5px" }}
                >
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{
                      width: `${processingStep * 25}%`,
                      background: "linear-gradient(to right, #2660a5, #4a8eda)",
                      borderRadius: "5px",
                    }}
                  ></div>
                </div>
                <div
                  className="py-2 px-3 mb-4 rounded"
                  style={{ backgroundColor: "rgba(38, 96, 165, 0.05)" }}
                >
                  <p
                    className="mb-0 font-weight-bold"
                    style={{ fontSize: "16px" }}
                  >
                    {processingStep === 1 && "Verifying your payment..."}
                    {processingStep === 2 &&
                      "Checking your support team members..."}
                    {processingStep === 3 && "Setting up your billing plan..."}
                    {processingStep === 4 && "Finalizing your subscription..."}
                  </p>
                </div>
                <p className="text-muted">
                  Please wait while we set up your subscription
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="mb-4 text-success">
                  <i
                    className="fas fa-check-circle"
                    style={{ fontSize: "5rem" }}
                  ></i>
                </div>
                <h2 className="mb-4">Subscription Successful!</h2>
                <p className="mb-4 lead">{message}</p>
                {subscriptionDetails && (
                  <div
                    className="text-left p-4 mb-4 rounded"
                    style={{
                      background: "linear-gradient(to right, #f8f9fa, #e9ecef)",
                      borderLeft: "4px solid #2660a5",
                    }}
                  >
                    <h5 className="mb-3 font-weight-bold">
                      Subscription Details:
                    </h5>
                    <Row>
                      <Col md={6}>
                        <div className="d-flex align-items-center mb-3">
                          <div className="usage-label" style={{ width: "40%" }}>
                            <span style={{ fontSize: "16px" }}>Plan:</span>
                          </div>
                          <div>
                            <span
                              className="badge p-2 ml-2"
                              style={{
                                fontSize: "16px",
                                backgroundColor: "#e9ecef",
                                color: "#333",
                              }}
                            >
                              {subscriptionDetails.plan}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex align-items-center mb-3">
                          <div className="usage-label" style={{ width: "40%" }}>
                            <span style={{ fontSize: "16px" }}>
                              Start Date:
                            </span>
                          </div>
                          <div>
                            <span
                              className="badge p-2 ml-2"
                              style={{
                                fontSize: "16px",
                                backgroundColor: "#e9ecef",
                                color: "#333",
                              }}
                            >
                              {new Date(
                                subscriptionDetails.startDate
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex align-items-center mb-3">
                          <div className="usage-label" style={{ width: "50%" }}>
                            <span style={{ fontSize: "16px" }}>Status:</span>
                          </div>
                          <div>
                            <span
                              className="badge p-2 ml-2"
                              style={{
                                fontSize: "16px",
                                backgroundColor: "#28a745",
                                color: "white",
                              }}
                            >
                              Active
                            </span>
                          </div>
                        </div>

                        {teamMemberCount > 0 && (
                          <div className="d-flex align-items-center mb-3">
                            <div
                              className="usage-label"
                              style={{ width: "50%" }}
                            >
                              <span style={{ fontSize: "16px" }}>
                                Team Members:
                              </span>
                            </div>
                            <div>
                              <span
                                className="badge p-2 ml-2"
                                style={{
                                  fontSize: "16px",
                                  backgroundColor:
                                    teamMemberCount > 100
                                      ? "#2660a5"
                                      : "#28a745",
                                  color: "white",
                                }}
                              >
                                {teamMemberCount}
                              </span>
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>

                    {teamMemberCount > 100 && (
                      <div
                        className="mt-3 pt-3 border-top"
                        style={{ borderColor: "#dee2e6" }}
                      >
                        <div style={{ fontSize: "16px" }}>
                          <strong>Billing:</strong> All {teamMemberCount}{" "}
                          members will be billed at $0.50 each.
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <p>Redirecting to your dashboard in a moment...</p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="mb-4 text-warning">
                  <i
                    className="fas fa-exclamation-circle"
                    style={{ fontSize: "5rem" }}
                  ></i>
                </div>
                <h2 className="mb-4">Something went wrong</h2>
                <Alert
                  variant="danger"
                  className="mb-4 border-0 shadow-sm"
                  style={{ borderRadius: "0.5rem" }}
                >
                  {message}
                </Alert>
                <div className="mt-4">
                  <p className="text-muted mb-2">
                    Please try again or contact support with the following
                    information:
                  </p>
                  <div
                    className="text-left bg-light p-3 rounded"
                    style={{ fontSize: "14px", fontFamily: "monospace" }}
                  >
                    Session ID: {sessionId || "Not available"}
                    <br />
                    Step: {processingStep}
                    <br />
                    Time: {new Date().toISOString()}
                  </div>
                </div>
              </>
            )}

            <div className="mt-4 pt-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleContinueToDashboard}
                className="px-5 py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
                  borderRadius: "2rem",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.12)",
                  border: "none",
                }}
              >
                Go to Dashboard
              </Button>
            </div>
          </Card.Body>
          <Card.Footer
            className="text-muted text-center py-4"
            style={{
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
            }}
          >
            <small>Thank you for subscribing to JobInfoNetwork!</small>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
